// src/pages/TermsOfUse.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  CheckCircle2,
  Clock3,
  FileCheck2,
  LockKeyhole,
  Scale,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import "../sass/pages/termosUso.scss";

const COMPANY = {
  legalName: "SANTANA'S DESENVOLVIMENTO E COMERCIO LTDA",
  tradeName: "PsyAssist Pro",
  document: "56.131.325/0001-68",
  website: "https://psyassist.com.br",
  supportEmail: "contato@psyassist.com.br",
  address:
    "Rua Doutor Isaac da Silva Bellini, 126, Parque Cidade Jardim, Jundiaí/SP, CEP 13203-522",
};

const TERMS_VERSION = "2026.08.05";
const TERMS_UPDATED_AT = "05/08/2026";

const SELECTED_PLAN_STORAGE_KEY = "psyassist:selected-plan";
const LEGACY_PLAN_STORAGE_KEY = "selectedPlan";
const REGISTRATION_STORAGE_KEY = "psyassist:registration-flow";

const API_URL = String(
  process.env.REACT_APP_API_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const EMPTY_PLAN_PAYLOAD = {
  planId: "",
  planName: "Plano não identificado",
  planPriceIndividual: "Valor não informado",
  planPriceClinica: "Valor não informado",
  planNormalPriceIndividual: "",
  planNormalPriceClinica: "",
  tipoUsuario: "individual",
  priceId: "",
  promotionalOffer: false,
  promotionalLabel: "",
  trialDays: 7,
  selectedAt: null,
};

function safeParseJson(value, fallback = null) {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Não foi possível interpretar os dados salvos:", error);
    return fallback;
  }
}

function getStoredPlan() {
  try {
    const currentValue = sessionStorage.getItem(SELECTED_PLAN_STORAGE_KEY);
    const legacyValue = sessionStorage.getItem(LEGACY_PLAN_STORAGE_KEY);

    return safeParseJson(currentValue || legacyValue, null);
  } catch (error) {
    console.error("Não foi possível acessar o sessionStorage:", error);
    return null;
  }
}

function savePlanFlow(payload) {
  try {
    const serialized = JSON.stringify(payload);

    sessionStorage.setItem(SELECTED_PLAN_STORAGE_KEY, serialized);
    sessionStorage.setItem(LEGACY_PLAN_STORAGE_KEY, serialized);
    sessionStorage.setItem(REGISTRATION_STORAGE_KEY, serialized);

    return true;
  } catch (error) {
    console.error("Não foi possível salvar o fluxo de cadastro:", error);
    return false;
  }
}

function normalizePlanPayload(source = {}) {
  return {
    planId: String(source.planId || source.id || "").trim(),
    planName: String(
      source.planName || source.name || EMPTY_PLAN_PAYLOAD.planName
    ).trim(),
    planPriceIndividual: String(
      source.planPriceIndividual ||
        source.pricePayload ||
        source.price ||
        EMPTY_PLAN_PAYLOAD.planPriceIndividual
    ).trim(),
    planPriceClinica: String(
      source.planPriceClinica ||
        source.planPriceIndividual ||
        source.pricePayload ||
        source.price ||
        EMPTY_PLAN_PAYLOAD.planPriceClinica
    ).trim(),
    planNormalPriceIndividual: String(
      source.planNormalPriceIndividual ||
        source.normalPricePayload ||
        ""
    ).trim(),
    planNormalPriceClinica: String(
      source.planNormalPriceClinica ||
        source.planNormalPriceIndividual ||
        source.normalPricePayload ||
        ""
    ).trim(),
    tipoUsuario: source.tipoUsuario === "clinica" ? "clinica" : "individual",
    priceId: String(source.priceId || "").trim(),
    promotionalOffer: source.promotionalOffer === true,
    promotionalLabel: String(source.promotionalLabel || "").trim(),
    trialDays: Math.max(Number(source.trialDays ?? 7), 0),
    selectedAt: source.selectedAt || null,
  };
}

function TermsDocument({ planPayload, displayedPlanPrice }) {
  return (
    <div className="terms-document__content">
      <p>
        Estes Termos de Uso e
        Contratação regulam o acesso
        e a utilização do{" "}
        <strong>
          PsyAssist Pro
        </strong>
        , plataforma digital
        destinada ao apoio da
        organização clínica,
        operacional, administrativa
        e financeira de profissionais
        e estabelecimentos da área de
        psicologia.
      </p>

      <p>
        O{" "}
        <strong>
          PsyAssist Pro
        </strong>{" "}
        é um produto desenvolvido,
        comercializado e mantido por{" "}
        <strong>
          {COMPANY.legalName}
        </strong>
        , pessoa jurídica inscrita no
        CNPJ sob o nº{" "}
        <strong>
          {COMPANY.document}
        </strong>
        , doravante denominada
        simplesmente{" "}
        <strong>
          “Contratada”
        </strong>
        .
      </p>

      <p>
        Ao marcar a opção de aceite e
        prosseguir com o cadastro, o
        usuário declara que leu,
        compreendeu e concorda
        integralmente com estes
        Termos.
      </p>

      <h2>
        1. Objeto e natureza da
        plataforma
      </h2>

      <p>
        O PsyAssist Pro fornece
        ferramentas tecnológicas para
        cadastro e organização de
        pacientes, agenda,
        agendamentos, prontuários,
        anamneses, registros de
        sessões, controle financeiro,
        leads, relatórios, lembretes,
        integrações e outros recursos
        compatíveis com o plano
        contratado.
      </p>

      <p>
        A plataforma constitui uma
        ferramenta de apoio
        organizacional e tecnológico.
        Ela não presta serviços de
        psicologia, atendimento
        clínico, diagnóstico,
        prescrição, tratamento,
        supervisão profissional,
        aconselhamento jurídico,
        contábil, fiscal ou
        financeiro.
      </p>

      <h2>
        2. Responsabilidade técnica do
        usuário
      </h2>

      <p>
        O usuário permanece
        integralmente responsável por
        sua atuação profissional, pelo
        conteúdo registrado na
        plataforma e por todas as
        decisões clínicas, técnicas,
        éticas, administrativas,
        financeiras e legais tomadas
        durante o exercício de sua
        atividade.
      </p>

      <p>
        Compete exclusivamente ao
        usuário avaliar a adequação,
        necessidade, legitimidade e
        exatidão das informações
        inseridas, consultadas,
        compartilhadas ou utilizadas
        por meio do sistema.
      </p>

      <p>
        A utilização da plataforma
        não transfere à Contratada a
        responsabilidade profissional
        do psicólogo, da clínica, de
        seus colaboradores ou de
        terceiros vinculados ao
        atendimento.
      </p>

      <h2>
        3. PSYA e funcionalidades de
        inteligência artificial
      </h2>

      <p>
        Determinados planos poderão
        oferecer a PSYA e outros
        recursos baseados em
        inteligência artificial,
        destinados a gerar resumos,
        análises, alertas, sugestões,
        organização de informações e
        possíveis próximos passos.
      </p>

      <p>
        As respostas produzidas por
        inteligência artificial podem
        conter limitações,
        imprecisões, interpretações
        incompletas ou informações
        que exijam validação. O
        usuário deve revisar
        criticamente qualquer
        conteúdo antes de utilizá-lo.
      </p>

      <p>
        A PSYA não substitui
        avaliação profissional,
        diagnóstico, prontuário,
        conduta clínica, decisão
        terapêutica, supervisão,
        consulta jurídica, contábil
        ou qualquer outra análise
        especializada.
      </p>

      <p>
        A Contratada não se
        responsabiliza por decisões
        tomadas exclusivamente com
        base em respostas,
        recomendações ou inferências
        geradas automaticamente sem a
        devida revisão humana e
        profissional.
      </p>

      <h2>
        4. Cadastro e credenciais
      </h2>

      <p>
        O usuário deverá fornecer
        informações verdadeiras,
        completas, atualizadas e
        compatíveis com sua identidade
        e atividade profissional.
      </p>

      <p>
        O usuário é responsável pela
        guarda e confidencialidade de
        suas credenciais, dispositivos
        e meios de autenticação,
        devendo comunicar
        imediatamente qualquer
        suspeita de acesso indevido.
      </p>

      <p>
        A conta é pessoal e
        intransferível, salvo quando o
        plano contratado permitir
        expressamente múltiplos
        usuários ou perfis de acesso.
      </p>

      <h2>
        5. Plano, período gratuito e
        cobrança
      </h2>

      <p>
        O usuário está selecionando o
        plano{" "}
        <strong>
          {planPayload.planName}
        </strong>
        , pelo valor atualmente
        apresentado de{" "}
        <strong>
          {displayedPlanPrice}
        </strong>
        .
      </p>

      <p>
        Quando aplicável, será
        concedido período gratuito de{" "}
        <strong>
          {planPayload.trialDays} dias
        </strong>
        . O cadastro de uma forma de
        pagamento poderá ser exigido
        antes do início do período
        gratuito.
      </p>

      <p>
        Caso a assinatura não seja
        cancelada antes do encerramento
        do período gratuito, a
        cobrança recorrente será
        iniciada automaticamente de
        acordo com o plano
        selecionado.
      </p>

      <p>
        O preço, a periodicidade e
        eventuais condições
        promocionais serão exibidos
        antes da confirmação da
        assinatura. Alterações futuras
        serão comunicadas conforme a
        legislação aplicável e não
        produzirão efeitos retroativos
        sobre cobranças já realizadas.
      </p>

      <h2>
        6. Cancelamento e inadimplência
      </h2>

      <p>
        A assinatura não possui
        fidelidade, salvo se houver
        condição comercial específica
        expressamente aceita pelo
        usuário.
      </p>

      <p>
        O cancelamento impede novas
        renovações, observadas as
        condições apresentadas no
        momento da contratação e os
        direitos previstos na
        legislação aplicável.
      </p>

      <p>
        A falta de pagamento poderá
        resultar em tentativas de
        cobrança, restrição de
        funcionalidades, suspensão ou
        encerramento do acesso,
        preservadas as obrigações
        legais relativas aos dados.
      </p>

      <h2>
        7. Dados de pacientes e
        informações clínicas
      </h2>

      <p>
        O usuário declara possuir
        fundamento jurídico,
        autorização profissional e
        legitimidade para inserir e
        tratar na plataforma os dados
        pessoais e dados pessoais
        sensíveis de seus pacientes,
        clientes, responsáveis,
        colaboradores e demais
        titulares.
      </p>

      <p>
        Em relação aos dados inseridos
        pelo usuário para execução de
        sua própria atividade, o
        usuário é responsável pelas
        decisões relativas à coleta,
        finalidade, conteúdo,
        atualização, compartilhamento,
        retenção e exclusão das
        informações.
      </p>

      <p>
        A Contratada realizará as
        operações necessárias à
        disponibilização,
        manutenção, segurança e
        execução da plataforma,
        observadas as instruções
        legítimas do usuário, a
        Política de Privacidade e a
        legislação aplicável.
      </p>

      <p>
        O usuário não deverá inserir
        informações excessivas,
        ilegais, discriminatórias,
        ofensivas ou sem relação com
        a finalidade legítima do
        atendimento e da gestão de sua
        atividade.
      </p>

      <h2>
        8. Privacidade e proteção de
        dados
      </h2>

      <p>
        O tratamento de dados pessoais
        será realizado conforme a
        legislação aplicável,
        especialmente a Lei nº
        13.709/2018 – Lei Geral de
        Proteção de Dados Pessoais.
      </p>

      <p>
        Os detalhes sobre categorias
        de dados, finalidades, bases
        legais, compartilhamentos,
        retenção, segurança e direitos
        dos titulares deverão ser
        consultados na Política de
        Privacidade do PsyAssist Pro,
        que integra estes Termos.
      </p>

      <p>
        Nenhum ambiente tecnológico é
        absolutamente imune a riscos.
        A Contratada adotará medidas
        técnicas e administrativas
        compatíveis com a natureza do
        serviço, sem garantir
        impossibilidade absoluta de
        incidentes, indisponibilidades
        ou ataques externos.
      </p>

      <h2>
        9. Integrações e serviços de
        terceiros
      </h2>

      <p>
        O PsyAssist Pro poderá
        integrar-se a serviços
        fornecidos por terceiros, como
        processadores de pagamento,
        serviços de agenda,
        videoconferência, e-mail,
        WhatsApp, armazenamento,
        hospedagem e provedores de
        inteligência artificial.
      </p>

      <p>
        A disponibilidade, regras,
        preços, limites, políticas e
        funcionamento desses serviços
        são definidos por seus
        respectivos fornecedores e
        poderão ser alterados,
        restringidos ou interrompidos
        sem controle direto da
        Contratada.
      </p>

      <p>
        A Contratada não responde por
        falhas exclusivamente
        atribuíveis a terceiros,
        incluindo indisponibilidade,
        bloqueio de conta, rejeição de
        mensagens, alteração de API,
        falhas de autenticação ou
        descumprimento das regras do
        fornecedor externo, sem
        prejuízo das responsabilidades
        que não possam ser legalmente
        afastadas.
      </p>

      <h2>
        10. Disponibilidade,
        manutenção e atualizações
      </h2>

      <p>
        A Contratada buscará manter a
        plataforma disponível e
        funcional, mas poderá realizar
        manutenções preventivas,
        corretivas, emergenciais ou
        evolutivas.
      </p>

      <p>
        Poderão ocorrer interrupções
        causadas por manutenção,
        atualização, falha de
        telecomunicações,
        infraestrutura externa,
        serviços de nuvem, caso
        fortuito, força maior, ataques
        cibernéticos ou outros eventos
        fora do controle razoável da
        Contratada.
      </p>

      <p>
        Funcionalidades poderão ser
        alteradas, substituídas,
        reorganizadas ou
        descontinuadas para preservar
        segurança, conformidade,
        viabilidade técnica ou
        evolução do produto.
      </p>

      <h2>
        11. Limitação de
        responsabilidade
      </h2>

      <p>
        Respeitados os limites da
        legislação aplicável, a
        Contratada não será
        responsável por:
      </p>

      <ul>
        <li>
          decisões clínicas,
          terapêuticas, profissionais,
          fiscais, contábeis,
          administrativas ou
          financeiras tomadas pelo
          usuário;
        </li>

        <li>
          conteúdo, exatidão,
          legalidade ou atualização
          dos dados inseridos pelo
          usuário;
        </li>

        <li>
          perda decorrente de uso
          inadequado da plataforma,
          compartilhamento de senha ou
          negligência do usuário;
        </li>

        <li>
          ausência de backup externo
          realizado pelo usuário
          quando essa providência for
          necessária à continuidade de
          sua própria atividade;
        </li>

        <li>
          falhas ou indisponibilidades
          exclusivamente atribuíveis a
          provedores, redes,
          dispositivos e serviços de
          terceiros;
        </li>

        <li>
          resultados esperados de
          natureza comercial,
          financeira, clínica ou de
          captação de pacientes;
        </li>

        <li>
          utilização de sugestões
          automatizadas sem revisão
          profissional adequada;
        </li>

        <li>
          atos ilícitos ou violações
          éticas praticadas pelo
          usuário ou por pessoas
          vinculadas à sua conta.
        </li>
      </ul>

      <p>
        Nenhuma disposição destes
        Termos excluirá ou limitará
        responsabilidade que não possa
        ser afastada por força de lei.
      </p>

      <h2>
        12. Uso proibido
      </h2>

      <p>
        É proibido utilizar a
        plataforma para prática de
        atos ilícitos, fraude,
        violação de direitos,
        discriminação, assédio,
        exposição indevida de dados,
        tentativa de invasão,
        engenharia reversa não
        autorizada, distribuição de
        código malicioso ou qualquer
        finalidade incompatível com
        estes Termos.
      </p>

      <h2>
        13. Propriedade intelectual
      </h2>

      <p>
        A marca, código-fonte,
        identidade visual, estrutura,
        textos, interfaces,
        funcionalidades, modelos,
        documentação e demais
        elementos do PsyAssist Pro
        pertencem à Contratada ou a
        seus licenciadores.
      </p>

      <p>
        A contratação concede apenas
        uma licença limitada,
        temporária, revogável, não
        exclusiva e intransferível
        para utilização da plataforma
        conforme o plano contratado.
      </p>

      <h2>
        14. Suspensão e encerramento
      </h2>

      <p>
        A Contratada poderá suspender
        ou encerrar contas em caso de
        fraude, inadimplência,
        violação destes Termos, risco
        à segurança, uso ilícito,
        ordem de autoridade competente
        ou necessidade de proteção da
        plataforma e de terceiros.
      </p>

      <p>
        Sempre que razoavelmente
        possível, o usuário será
        previamente comunicado e terá
        oportunidade de regularizar a
        situação.
      </p>

      <h2>
        15. Alteração dos termos
      </h2>

      <p>
        Estes Termos poderão ser
        atualizados para refletir
        alterações legais, técnicas,
        comerciais, operacionais ou de
        segurança.
      </p>

      <p>
        Mudanças relevantes serão
        comunicadas por meios
        adequados. Quando necessário,
        poderá ser solicitado novo
        aceite antes da continuidade
        do uso.
      </p>

      <h2>
        16. Comunicações e suporte
      </h2>

      <p>
        Comunicações relacionadas à
        conta, assinatura, segurança,
        cobrança e funcionamento da
        plataforma poderão ser
        enviadas por e-mail,
        notificações internas ou
        outros canais informados pelo
        usuário.
      </p>

      <p>
        O canal corporativo de contato
        é{" "}
        <strong>
          {COMPANY.supportEmail}
        </strong>
        .
      </p>

      <h2>
        17. Legislação aplicável e
        solução de conflitos
      </h2>

      <p>
        Estes Termos são regidos pelas
        leis da República Federativa
        do Brasil.
      </p>

      <p>
        As partes buscarão resolver
        eventuais divergências
        inicialmente por meio dos
        canais de atendimento da
        Contratada, sem prejuízo dos
        direitos assegurados ao
        consumidor e das regras legais
        de competência aplicáveis.
      </p>

      <h2>
        18. Identificação da
        fornecedora
      </h2>

      <p>
        <strong>
          Produto:
        </strong>{" "}
        PsyAssist Pro
      </p>

      <p>
        <strong>
          Desenvolvedora e
          fornecedora:
        </strong>{" "}
        {COMPANY.legalName}
      </p>

      <p>
        <strong>
          CNPJ:
        </strong>{" "}
        {COMPANY.document}
      </p>

      <p>
        <strong>
          Endereço:
        </strong>{" "}
        {COMPANY.address}
      </p>

      <p>
        <strong>
          Website:
        </strong>{" "}
        {COMPANY.website}
      </p>

      <p>
        <strong>
          Contato:
        </strong>{" "}
        {COMPANY.supportEmail}
      </p>

      <p className="terms-final-paragraph">
        Ao selecionar a caixa de
        aceite e prosseguir, o usuário
        confirma que leu, compreendeu
        e concorda integralmente com
        estes Termos de Uso e
        Contratação, com a Política de
        Privacidade e com as condições
        comerciais do plano
        selecionado.
      </p>
    </div>
  );
}

export default function TermsOfUse() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const termsScrollRef = useRef(null);

  const [aceitaTermos, setAceitaTermos] = useState(false);
  const [acceptError, setAcceptError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [planPayload, setPlanPayload] = useState(EMPTY_PLAN_PAYLOAD);

  useEffect(() => {
    const navigationState =
      location.state && typeof location.state === "object"
        ? location.state
        : null;

    const storedPlan = getStoredPlan();
    const source = navigationState?.priceId
      ? navigationState
      : storedPlan || {};

    const normalized = normalizePlanPayload(source);

    setPlanPayload(normalized);

    if (normalized.planId && normalized.priceId) {
      savePlanFlow(normalized);
    }
  }, [location.state]);

  const displayedPlanPrice = useMemo(
    () =>
      planPayload.tipoUsuario === "clinica"
        ? planPayload.planPriceClinica
        : planPayload.planPriceIndividual,
    [planPayload]
  );

  const usuarioLabel =
    planPayload.tipoUsuario === "clinica"
      ? "Clínica"
      : "Profissional individual";

  const planoValido = Boolean(
    planPayload.planId &&
      planPayload.planName &&
      planPayload.priceId
  );

  const updateReadingProgress = () => {
    const element = termsScrollRef.current;

    if (!element) {
      return;
    }

    const available = element.scrollHeight - element.clientHeight;

    if (available <= 0) {
      setReadingProgress(100);
      return;
    }

    setReadingProgress(
      Math.min(100, Math.round((element.scrollTop / available) * 100))
    );
  };

  const criarCadastroTemporario = async () => {
    const body = {
      termosAceitos: true,
      termosVersao: TERMS_VERSION,
      termosAceitosEm: new Date().toISOString(),
      plano: planPayload.planName,
      planId: planPayload.planId,
      priceId: planPayload.priceId,
      tipoUsuario: planPayload.tipoUsuario,
      trialDays: planPayload.trialDays,
      origemCadastro: "landing-page",
    };

    try {
      const response = await fetch(`${API_URL}/psy/temp-register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const raw = await response.text();
      const json = safeParseJson(raw, null);

      if (response.ok && json?.sessionId) {
        return json.sessionId;
      }

      toast.error({
        title: "Cadastro não iniciado",
        message:
          json?.message ||
          json?.error ||
          `Não foi possível iniciar o cadastro. Código ${response.status}.`,
        duration: 0,
      });

      return null;
    } catch (error) {
      console.error("Erro ao criar cadastro temporário:", error);

      toast.error({
        title: "Falha de comunicação",
        message:
          "Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.",
        duration: 0,
      });

      return null;
    }
  };

  const handleAdvance = async () => {
    if (isSubmitting) {
      return;
    }

    if (!planoValido) {
      toast.warning({
        title: "Plano não identificado",
        message:
          "Volte à seleção de planos e escolha novamente a modalidade desejada.",
        actionLabel: "Voltar aos planos",
        onAction: () => navigate(-1),
        duration: 0,
      });

      return;
    }

    if (!aceitaTermos) {
      setAcceptError(true);

      toast.warning({
        title: "Aceite necessário",
        message:
          "Confirme a leitura dos Termos de Uso, da Política de Privacidade e das condições comerciais.",
      });

      document
        .querySelector(".terms-consent")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });

      return;
    }

    setAcceptError(false);
    setIsSubmitting(true);

    try {
      const sessionId = await criarCadastroTemporario();

      if (!sessionId) {
        return;
      }

      const registrationPayload = {
        ...planPayload,
        sessionId,
        termosAceitos: true,
        termosVersao: TERMS_VERSION,
        termosAceitosEm: new Date().toISOString(),
        companyLegalName: COMPANY.legalName,
        companyDocument: COMPANY.document,
      };

      const saved = savePlanFlow(registrationPayload);

      if (!saved) {
        console.warn(
          "O fluxo não foi salvo no sessionStorage e seguirá pelo state da navegação."
        );
      }

      toast.success({
        title: "Termos aceitos",
        message: "Sua contratação está pronta para avançar ao cadastro.",
      });

      navigate("/cadastro", {
        state: registrationPayload,
        replace: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="terms-page">
      <div className="terms-page__ambient" aria-hidden="true">
        <span className="terms-page__glow terms-page__glow--one" />
        <span className="terms-page__glow terms-page__glow--two" />
        <span className="terms-page__grid" />
      </div>

      <div className="terms-shell">
        <header className="terms-topbar">
          <button
            type="button"
            className="terms-back-link"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            <ArrowLeft size={17} />
            <span>Voltar</span>
          </button>

          <img src={Logo} alt="PsyAssist Pro" className="terms-brand" />

          <div className="terms-security-label">
            <LockKeyhole size={15} />
            <span>Ambiente seguro</span>
          </div>
        </header>

        <nav className="terms-steps" aria-label="Etapas da contratação">
          <div className="terms-step terms-step--complete">
            <span><Check size={14} /></span>
            <div>
              <small>Etapa 1</small>
              <strong>Plano</strong>
            </div>
          </div>

          <div className="terms-step__line terms-step__line--active" />

          <div className="terms-step terms-step--active">
            <span>2</span>
            <div>
              <small>Etapa 2</small>
              <strong>Termos</strong>
            </div>
          </div>

          <div className="terms-step__line" />

          <div className="terms-step">
            <span>3</span>
            <div>
              <small>Etapa 3</small>
              <strong>Cadastro</strong>
            </div>
          </div>
        </nav>

        <section className="terms-hero">
          <div>
            <span className="terms-kicker">
              <Sparkles size={15} />
              Contratação transparente
            </span>

            <h1>Revise os termos antes de continuar.</h1>

            <p>
              Organizamos as condições da plataforma, da assinatura e dos
              recursos de inteligência artificial em um único documento.
            </p>
          </div>

          <div className="terms-hero__trust">
            <ShieldCheck size={21} />
            <div>
              <strong>Seus dados exigem responsabilidade.</strong>
              <span>
                O PsyAssist Pro foi estruturado para apoiar a gestão profissional
                sem substituir decisões clínicas.
              </span>
            </div>
          </div>
        </section>

        {!planoValido ? (
          <section className="terms-plan-alert" role="alert">
            <div className="terms-plan-alert__icon">
              <FileCheck2 size={20} />
            </div>

            <div>
              <strong>Não foi possível identificar o plano selecionado.</strong>
              <p>
                A contratação não pode continuar sem o identificador operacional
                do plano e da cobrança.
              </p>
            </div>

            <button type="button" onClick={() => navigate(-1)}>
              Escolher plano
              <ArrowRight size={16} />
            </button>
          </section>
        ) : null}

        <div className="terms-layout">
          <section className="terms-document">
            <header className="terms-document__header">
              <div>
                <span className="terms-document__eyebrow">
                  Documento contratual
                </span>
                <h2>Termos de Uso e Contratação</h2>
                <p>
                  Versão {TERMS_VERSION} · Atualizado em {TERMS_UPDATED_AT}
                </p>
              </div>

              <div className="terms-reading">
                <span>{readingProgress}% lido</span>
                <div className="terms-reading__track">
                  <span style={{ width: `${readingProgress}%` }} />
                </div>
              </div>
            </header>

            <div
              ref={termsScrollRef}
              className="terms-document__scroll"
              onScroll={updateReadingProgress}
              tabIndex={0}
              aria-label="Conteúdo dos Termos de Uso e Contratação"
            >
              <TermsDocument
                planPayload={planPayload}
                displayedPlanPrice={displayedPlanPrice}
              />
            </div>

            <footer className="terms-document__footer">
              <Scale size={17} />
              <span>
                Dúvidas contratuais podem ser encaminhadas para{" "}
                <strong>{COMPANY.supportEmail}</strong>.
              </span>
            </footer>
          </section>

          <aside className="terms-summary">
            <div className="terms-summary__card">
              <div className="terms-summary__head">
                <div className="terms-summary__icon">
                  {planPayload.tipoUsuario === "clinica" ? (
                    <Building2 size={20} />
                  ) : (
                    <BadgeCheck size={20} />
                  )}
                </div>

                <div>
                  <span>Plano selecionado</span>
                  <h2>{planPayload.planName}</h2>
                </div>
              </div>

              <div className="terms-summary__price">
                <small>Valor apresentado</small>
                <strong>{displayedPlanPrice}</strong>
                <span>{usuarioLabel}</span>
              </div>

              {planPayload.promotionalLabel ? (
                <div className="terms-summary__promotion">
                  <Sparkles size={15} />
                  <span>{planPayload.promotionalLabel}</span>
                </div>
              ) : null}

              <div className="terms-summary__facts">
                <div>
                  <Clock3 size={17} />
                  <span>
                    <strong>{planPayload.trialDays} dias</strong>
                    de período gratuito
                  </span>
                </div>

                <div>
                  <CheckCircle2 size={17} />
                  <span>
                    <strong>Sem fidelidade</strong>
                    salvo condição específica
                  </span>
                </div>

                <div>
                  <ShieldCheck size={17} />
                  <span>
                    <strong>Proteção de dados</strong>
                    conforme regras aplicáveis
                  </span>
                </div>
              </div>
            </div>

            <section
              className={`terms-consent ${
                acceptError ? "terms-consent--error" : ""
              }`}
            >
              <label className="terms-consent__label">
                <input
                  type="checkbox"
                  checked={aceitaTermos}
                  onChange={(event) => {
                    setAceitaTermos(event.target.checked);
                    setAcceptError(false);
                  }}
                  aria-invalid={acceptError}
                />

                <span className="terms-consent__check" aria-hidden="true">
                  <Check size={15} />
                </span>

                <span>
                  Declaro que li e aceito os Termos de Uso e Contratação, a
                  Política de Privacidade e as condições do plano selecionado.
                </span>
              </label>

              {acceptError ? (
                <p role="alert">
                  Confirme o aceite para prosseguir com a contratação.
                </p>
              ) : null}
            </section>

            <div className="terms-actions">
              <button
                type="button"
                className="terms-primary-button"
                onClick={handleAdvance}
                disabled={isSubmitting || !planoValido}
              >
                {isSubmitting ? (
                  <>
                    <span className="terms-button-spinner" aria-hidden="true" />
                    Processando
                  </>
                ) : (
                  <>
                    Aceitar e continuar
                    <ArrowRight size={17} />
                  </>
                )}
              </button>

              <button
                type="button"
                className="terms-secondary-button"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                Voltar aos planos
              </button>
            </div>

            <p className="terms-summary__legal-note">
              Ao continuar, o aceite será registrado com data, horário e versão
              vigente dos termos.
            </p>
          </aside>
        </div>
      </div>
    </main>
  );
}
